import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError, uploadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Primitives";
import { RichTextEditor } from "@/components/RichTextEditor";
import { ClipboardList, Plus, Trash2, FolderPlus, ChevronLeft, Pencil, Search, Paperclip, FileText, GripVertical, Upload, Image as ImageIcon, Star } from "lucide-react";

const inp = "w-full px-3 py-2 bg-white border border-[#E5E5E2] rounded-sm text-sm focus:ring-2 focus:ring-[#F7941D] focus:outline-none";
const lbl = "text-[11px] tracking-[0.12em] uppercase font-semibold text-[#575755] block mb-1";
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

export default function OfficeProcedures() {
  const { user } = useAuth();
  const canWrite = user.role === "admin" || user.access?.office_procedures === "write";
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState(null);
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");
  const [dragCat, setDragCat] = useState(null);
  const [dragDoc, setDragDoc] = useState(null);
  const [favs, setFavs] = useState([]);
  useEffect(() => { api.get("/procedures/favorites").then((r) => setFavs(r.data)).catch(() => {}); }, []);
  const toggleFav = async (d) => {
    const on = !favs.includes(d.id);
    setFavs((f) => (on ? [...f, d.id] : f.filter((x) => x !== d.id)));
    try { const { data } = await api.put(`/procedures/${d.id}/favorite`, { favorite: on }); setFavs(data); toast.success(on ? "Added to your favorites" : "Removed from your favorites"); }
    catch { toast.error("Couldn't update favorites"); }
  };

  const persistCategories = async (list) => {
    setCategories(list);
    try { await api.put("/procedures/categories/reorder", { order: list.map((c) => c.id) }); }
    catch { toast.error("Couldn't save category order"); load(); }
  };
  const persistDocs = async (list) => {
    setDocs(list);
    try { await api.put("/procedures/reorder", { items: list.map((d) => ({ id: d.id, category_id: d.category_id || null })) }); }
    catch { toast.error("Couldn't save order"); load(); }
  };
  const onDropCategory = (targetId) => {
    if (dragCat == null || targetId == null || dragCat === targetId) { setDragCat(null); return; }
    const arr = [...categories];
    const from = arr.findIndex((c) => c.id === dragCat);
    const to = arr.findIndex((c) => c.id === targetId);
    if (from < 0 || to < 0) { setDragCat(null); return; }
    const [m] = arr.splice(from, 1); arr.splice(to, 0, m);
    persistCategories(arr); setDragCat(null);
  };
  const onDropCard = (target) => {
    if (!dragDoc || dragDoc === target.id) { setDragDoc(null); return; }
    const arr = [...docs];
    const di = arr.findIndex((x) => x.id === dragDoc);
    if (di < 0) { setDragDoc(null); return; }
    const moved = { ...arr[di], category_id: target.category_id || null };
    arr.splice(di, 1);
    const ti = arr.findIndex((x) => x.id === target.id);
    arr.splice(ti, 0, moved);
    persistDocs(arr); setDragDoc(null);
  };
  const onDropIntoCategory = (catId) => {
    if (!dragDoc) return;
    const arr = [...docs];
    const di = arr.findIndex((x) => x.id === dragDoc);
    if (di < 0 || (arr[di].category_id || null) === (catId || null)) { setDragDoc(null); return; }
    const moved = { ...arr[di], category_id: catId || null };
    arr.splice(di, 1);
    let lastIdx = -1;
    arr.forEach((x, idx) => { if ((x.category_id || null) === (catId || null)) lastIdx = idx; });
    arr.splice(lastIdx + 1, 0, moved);
    persistDocs(arr); setDragDoc(null);
  };

  const load = useCallback(async () => {
    const [d, c] = await Promise.all([api.get("/procedures"), api.get("/procedures/categories")]);
    setDocs(d.data); setCategories(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this procedure?")) return;
    await api.delete(`/procedures/${id}`); toast.success("Deleted"); load();
  };
  const addCategory = async () => {
    const name = window.prompt("New category name"); if (!name) return;
    await api.post("/procedures/categories", { name }); load();
  };
  const renameCategory = async (cat) => {
    const name = window.prompt("Rename category", cat.name); if (!name || name === cat.name) return;
    await api.put(`/procedures/categories/${cat.id}`, { name }); toast.success("Category renamed"); load();
  };
  const deleteCategory = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"?`)) return;
    try { await api.delete(`/procedures/categories/${cat.id}`); toast.success("Category deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Move the procedures out of this category first."); }
  };
  const setCover = async (cat, e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Pick an image file (JPG, PNG or WebP)"); return; }
    try {
      const d = await uploadFile(f, "/procedures/upload");
      await api.put(`/procedures/categories/${cat.id}`, { cover_url: d.url });
      toast.success("Cover image saved"); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
  };
  const removeCover = async (cat) => {
    await api.put(`/procedures/categories/${cat.id}`, { cover_url: "" }); toast.success("Cover image removed"); load();
  };

  const q = query.trim().toLowerCase();
  const matches = (d) => !q || `${d.title || ""} ${(d.body || "").replace(/<[^>]+>/g, " ")}`.toLowerCase().includes(q);
  const grouped = [{ id: null, name: "Uncategorized" }, ...categories].map((c) => ({
    category: c, items: docs.filter((d) => (d.category_id || null) === c.id && matches(d)),
  })).filter((g) => (q ? g.items.length : (g.category.id !== null || g.items.length)));

  if (active) return <ProcedureView doc={active} onBack={() => setActive(null)} />;
  if (editing) return <ProcedureEditor doc={editing} categories={categories} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;

  return (
    <div data-testid="procedures-page">
      <PageHeader title="Office Procedures" subtitle="Company SOPs and how-we-do-it documents for the team."
        action={canWrite && (
          <div className="flex gap-2">
            <button data-testid="add-category-btn" onClick={addCategory} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#E5E5E2] text-sm font-semibold hover:bg-[#F0EFEC]"><FolderPlus className="w-4 h-4" /> Category</button>
            <button data-testid="new-procedure-btn" onClick={() => setEditing({ title: "", body: "", category_id: null, file: null })} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#F7941D] text-white text-sm font-semibold hover:bg-[#E08312]"><Plus className="w-4 h-4" /> New Procedure</button>
          </div>
        )} />

      {docs.length === 0 && categories.length === 0 && (
        <div className="text-center py-24 text-[#8a8a86]"><ClipboardList className="w-10 h-10 mx-auto mb-3 text-[#C9C9C4]" />No procedures yet.</div>
      )}

      {docs.length > 0 && (
        <div className="relative mb-8 max-w-md">
          <Search className="w-4 h-4 text-[#8a8a86] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="procedures-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search procedures…"
            className="w-full pl-9 pr-3 py-2.5 bg-white border border-[#E5E5E2] rounded-full text-sm focus:ring-2 focus:ring-[#F7941D] focus:outline-none" />
        </div>
      )}

      {q && grouped.length === 0 && <div className="text-sm text-[#8a8a86]" data-testid="procedures-no-results">No procedures match "{query}".</div>}

      {(() => {
        const favDocs = favs.map((id) => docs.find((d) => d.id === id)).filter((d) => d && matches(d));
        if (!favDocs.length) return null;
        return (
          <div className="mb-10" data-testid="procedures-favorites">
            <div className="flex items-center gap-2 mb-4">
              <Star className="w-4 h-4 text-[#F7941D] fill-[#F7941D]" />
              <h3 className="text-sm font-semibold text-[#0A0A0A]">My Favorites</h3>
              <span className="text-xs text-[#8a8a86]">{favDocs.length}</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{favDocs.map((d) => renderCard(d, true))}</div>
          </div>
        );
      })()}

      {grouped.map((g) => (
        <div key={g.category.id || "none"} className="mb-10">
          <div
            className={`group/cat mb-4 ${g.category.cover_url ? "relative h-32 sm:h-36 rounded-sm overflow-hidden flex items-end px-5 pb-4" : "flex items-center gap-2"}`}
            data-testid={`category-header-${g.category.id || "uncategorized"}`}
            draggable={canWrite && !q && g.category.id !== null}
            onDragStart={() => setDragCat(g.category.id)}
            onDragOver={(e) => { if (canWrite && !q && dragCat != null) e.preventDefault(); }}
            onDrop={() => { if (canWrite && !q) onDropCategory(g.category.id); }}
          >
            {g.category.cover_url && (
              <>
                <img src={absUrl(g.category.cover_url)} alt="" draggable={false} data-testid={`category-cover-${g.category.id}`} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
              </>
            )}
            <div className={`flex items-center gap-2 ${g.category.cover_url ? "z-10" : ""}`}>
            {canWrite && !q && g.category.id !== null && <GripVertical data-testid={`category-drag-${g.category.id}`} className={`w-4 h-4 cursor-grab shrink-0 ${g.category.cover_url ? "text-white/70" : "text-[#C9C9C4]"}`} />}
            <h3 data-testid={`category-title-${g.category.id || "uncategorized"}`} className={g.category.cover_url ? "font-serif text-2xl text-white drop-shadow" : "text-sm font-semibold text-[#0A0A0A]"}>{g.category.name}</h3>
            <span className={`text-xs ${g.category.cover_url ? "text-white/80" : "text-[#8a8a86]"}`}>{g.items.length}</span>
            {canWrite && g.category.id !== null && (
              <span className={`flex items-center gap-1.5 transition-opacity ${g.category.cover_url ? "absolute top-3 right-3 z-10 text-white bg-black/35 backdrop-blur-sm rounded-full px-2.5 py-1 sm:opacity-0 sm:group-hover/cat:opacity-100" : "opacity-0 group-hover/cat:opacity-100 focus-within:opacity-100"}`}>
                <button data-testid={`category-rename-${g.category.id}`} onClick={() => renameCategory(g.category)} className={g.category.cover_url ? "hover:text-[#F7941D]" : "text-[#8a8a86] hover:text-[#1F3C88]"} title="Rename category"><Pencil className="w-3.5 h-3.5" /></button>
                <label data-testid={`category-cover-upload-${g.category.id}`} className={`cursor-pointer ${g.category.cover_url ? "hover:text-[#F7941D]" : "text-[#8a8a86] hover:text-[#1F3C88]"}`} title={g.category.cover_url ? "Change cover image" : "Add cover image"}>
                  <ImageIcon className="w-3.5 h-3.5" /><input type="file" accept="image/*" hidden onChange={(e) => setCover(g.category, e)} />
                </label>
                {g.category.cover_url && <button data-testid={`category-cover-remove-${g.category.id}`} onClick={() => removeCover(g.category)} className="text-[11px] font-semibold hover:text-[#F7941D]">Remove cover</button>}
                <button data-testid={`category-delete-${g.category.id}`} onClick={() => deleteCategory(g.category)} className={g.category.cover_url ? "hover:text-[#F7941D]" : "text-[#8a8a86] hover:text-[#992B2B]"} title="Delete category"><Trash2 className="w-3.5 h-3.5" /></button>
              </span>
            )}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
            onDragOver={(e) => { if (canWrite && !q && dragDoc) e.preventDefault(); }}
            onDrop={() => { if (canWrite && !q) onDropIntoCategory(g.category.id); }}>
            {g.items.map((d) => renderCard(d, false))}
            {g.items.length === 0 && <p className="text-sm text-[#8a8a86] col-span-full">No procedures in this category yet.</p>}
          </div>
        </div>
      ))}
    </div>
  );

  function renderCard(d, inFav) {
    const drag = canWrite && !q && !inFav;
    const fav = favs.includes(d.id);
    return (
              <div key={`${inFav ? "fav-" : ""}${d.id}`} data-testid={`${inFav ? "fav-" : ""}procedure-card-${d.id}`}
                draggable={drag}
                onDragStart={(e) => { e.stopPropagation(); setDragDoc(d.id); }}
                onDragOver={(e) => { if (drag && dragDoc) e.preventDefault(); }}
                onDrop={(e) => { e.stopPropagation(); if (drag) onDropCard(d); }}
                className="relative bg-white border border-[#E5E5E2] rounded-sm overflow-hidden group hover:border-[#F7941D] transition-colors duration-200">
                <button type="button" onClick={() => toggleFav(d)} data-testid={`${inFav ? "fav-" : ""}procedure-star-${d.id}`}
                  title={fav ? "Remove from my favorites" : "Add to my favorites"} aria-pressed={fav}
                  className={`absolute top-2.5 right-2.5 z-10 p-1.5 rounded-full transition-colors ${d.thumbnail_url ? "bg-white/85 backdrop-blur-sm" : ""} ${fav ? "text-[#F7941D]" : "text-[#C9C9C4] hover:text-[#F7941D]"}`}>
                  <Star className={`w-4 h-4 ${fav ? "fill-[#F7941D]" : ""}`} />
                </button>
                <button onClick={() => setActive(d)} className="block w-full text-left">
                  {d.thumbnail_url && (
                    <div className="h-36 bg-[#F0EFEC] overflow-hidden" data-testid={`${inFav ? "fav-" : ""}procedure-thumb-${d.id}`}>
                      <img src={absUrl(d.thumbnail_url)} alt="" draggable={false} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
                    </div>
                  )}
                  <div className="flex items-start gap-3 p-5 pr-10">
                    {drag && <GripVertical className="w-4 h-4 text-[#C9C9C4] cursor-grab shrink-0 mt-1" />}
                    {!d.thumbnail_url && <div className="w-10 h-10 rounded-sm bg-[#EAF0FB] flex items-center justify-center shrink-0"><FileText className="w-5 h-5 text-[#1F3C88]" /></div>}
                    <div className="min-w-0">
                      <div className="font-semibold text-[#0A0A0A] truncate">{d.title}</div>
                      <p className="text-sm text-[#575755] mt-1 line-clamp-2">{(d.body || "").replace(/<[^>]+>/g, " ").trim() || "Open procedure"}</p>
                      {d.file && <span className="inline-flex items-center gap-1 text-xs text-[#8a8a86] mt-2"><Paperclip className="w-3 h-3" /> Attachment</span>}
                    </div>
                  </div>
                </button>
                {canWrite && (
                  <div className="flex border-t border-[#E5E5E2]">
                    <button onClick={() => setEditing(d)} className="flex-1 py-2 text-xs font-semibold text-[#1F3C88] hover:bg-[#F8FAFE]">Edit</button>
                    <button onClick={() => del(d.id)} data-testid={`${inFav ? "fav-" : ""}delete-procedure-${d.id}`} className="flex-1 py-2 text-xs font-semibold text-[#992B2B] hover:bg-[#F6E4E4] border-l border-[#E5E5E2]">Delete</button>
                  </div>
                )}
              </div>
    );
  }
}

function ProcedureView({ doc, onBack }) {
  const bodyRef = useRef(null);
  useEffect(() => {
    if (!bodyRef.current) return;
    bodyRef.current.querySelectorAll("a").forEach((a) => {
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
    });
  }, [doc]);
  return (
    <div data-testid="procedure-view" className="max-w-3xl">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-[#575755] hover:text-[#1F3C88] mb-6"><ChevronLeft className="w-4 h-4" /> All procedures</button>
      {doc.thumbnail_url && <img src={absUrl(doc.thumbnail_url)} alt="" data-testid="procedure-view-thumb" className="w-full max-h-64 object-cover rounded-sm mb-6" />}
      <h1 className="font-serif text-3xl text-[#0A0A0A]">{doc.title}</h1>
      {doc.file && (
        <a href={absUrl(doc.file.url || doc.file)} target="_blank" rel="noopener noreferrer" data-testid="procedure-attachment"
          className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-full border border-[#E5E5E2] text-sm font-semibold text-[#1F3C88] hover:bg-[#F8FAFE]">
          <Paperclip className="w-4 h-4" /> {doc.file.name || "View attachment"}
        </a>
      )}
      <div className="prose prose-sm max-w-none mt-6 text-[#333] leading-relaxed [&_h1]:font-serif [&_h2]:font-serif [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-[#1F3C88] [&_a]:underline [&_a]:break-words"
        ref={bodyRef} data-testid="procedure-body" dangerouslySetInnerHTML={{ __html: doc.body || "<p>No content yet.</p>" }} />
    </div>
  );
}

function ProcedureEditor({ doc, categories, onClose, onSaved }) {
  const [form, setForm] = useState({ ...doc });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadAttachment = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const d = await uploadFile(f, "/procedures/upload"); set("file", { url: d.url, name: f.name }); toast.success("Attachment uploaded"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { e.target.value = ""; }
  };

  const uploadThumb = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Pick an image file (JPG, PNG or WebP)"); e.target.value = ""; return; }
    try { const d = await uploadFile(f, "/procedures/upload"); set("thumbnail_url", d.url); toast.success("Thumbnail uploaded"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { e.target.value = ""; }
  };

  const save = async () => {
    if (!(form.title || "").trim()) { toast.error("Add a title"); return; }
    setSaving(true);
    try {
      if (form.id) await api.put(`/procedures/${form.id}`, form);
      else await api.post("/procedures", form);
      toast.success(form.id ? "Procedure saved" : "Procedure created");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="procedure-editor" className="max-w-3xl">
      <button onClick={onClose} className="inline-flex items-center gap-2 text-sm text-[#575755] hover:text-[#1F3C88] mb-6"><ChevronLeft className="w-4 h-4" /> Back</button>
      <h1 className="font-serif text-3xl text-[#0A0A0A] mb-6">{form.id ? "Edit Procedure" : "New Procedure"}</h1>
      <div className="space-y-4 bg-white border border-[#E5E5E2] rounded-sm p-5">
        <div><span className={lbl}>Title</span><input data-testid="procedure-title" className={inp} value={form.title || ""} onChange={(e) => set("title", e.target.value)} /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <span className={lbl}>Category</span>
            <select data-testid="procedure-category" className={inp} value={form.category_id || ""} onChange={(e) => set("category_id", e.target.value || null)}>
              <option value="">Uncategorized</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <span className={lbl}>Attachment (optional)</span>
            <div className="flex items-center gap-2">
              {form.file && <span className="text-xs text-[#1F8A4C] truncate max-w-[120px]">{form.file.name || "file"}</span>}
              <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border border-[#E5E5E2] text-sm cursor-pointer hover:bg-[#F0EFEC]"><Paperclip className="w-4 h-4" /> Upload<input type="file" hidden onChange={uploadAttachment} /></label>
              {form.file && <button type="button" className="text-xs text-[#992B2B] hover:underline" onClick={() => set("file", null)}>remove</button>}
            </div>
          </div>
        </div>
        <div>
          <span className={lbl}>Thumbnail (optional)</span>
          <div className="flex items-center gap-3">
            {form.thumbnail_url
              ? <img src={absUrl(form.thumbnail_url)} alt="" data-testid="procedure-thumb-preview" className="w-24 h-14 rounded-sm object-cover border border-[#E5E5E2]" />
              : <div className="w-24 h-14 rounded-sm bg-[#F0EFEC] grid place-items-center"><ImageIcon className="w-5 h-5 text-[#C9C9C4]" /></div>}
            <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border border-[#E5E5E2] text-sm cursor-pointer hover:bg-[#F0EFEC]"><Upload className="w-4 h-4" /> {form.thumbnail_url ? "Replace" : "Upload"}<input data-testid="procedure-thumb-input" type="file" accept="image/*" hidden onChange={uploadThumb} /></label>
            {form.thumbnail_url && <button type="button" data-testid="procedure-thumb-remove" className="text-xs text-[#992B2B] hover:underline" onClick={() => set("thumbnail_url", "")}>remove</button>}
          </div>
          <p className="text-[11px] text-[#8a8a86] mt-1">Shown at the top of the procedure card. A wide image (about 3:2) looks best.</p>
        </div>
        <div>
          <span className={lbl}>Procedure</span>
          <RichTextEditor testid="procedure-body-editor" value={form.body || ""} onChange={(v) => set("body", v)} placeholder="Write the procedure — steps, links, notes…" />
        </div>
      </div>
      <div className="mt-6 flex justify-end">
        <button onClick={save} disabled={saving} data-testid="save-procedure-btn" className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full bg-[#F7941D] text-white text-sm font-semibold hover:bg-[#E08312] disabled:opacity-60">{saving ? "Saving…" : (form.id ? "Save Procedure" : "Create Procedure")}</button>
      </div>
    </div>
  );
}
